import {
  useState,
  type FocusEvent,
  type KeyboardEvent,
  type MouseEvent,
  type ReactElement,
} from "react";
import { motion } from "framer-motion";
import { MoreVertical } from "lucide-react";
import {
  color,
  motion as motionTokens,
  radius,
  useListItemMotion,
  usePrefersReducedMotion,
} from "@tokens/index";
import { IconButton } from "@primitives/IconButton";
import { motionStyle } from "@primitives/motionStyle";
import { Text } from "@primitives/Text";
import type { QueueSummary } from "../../../experience-shell/queues";
import { listRow, selectionBar } from "./listSurface";
import styles from "./QueueListRow.module.css";

/**
 * One row of the queue rail: personal marker, name, open count, and the
 * actions kebab. The parent owns roving focus and the single `Menu`.
 */

export interface QueueListRowProps {
  readonly queue: QueueSummary;
  readonly index: number;
  readonly selected: boolean;
  /** The row is in the Tab sequence. Exactly one row in the list is. */
  readonly tabbable: boolean;
  readonly onSelect?: (queue: QueueSummary) => void;
  readonly onFocusRow: (index: number) => void;
  readonly rowRef: (element: HTMLDivElement | null) => void;
  readonly openCount: number | undefined;
  readonly countLoading: boolean;
  readonly hasActions: boolean;
  /** Show the kebab at rest. Set on coarse pointers, where hover does not exist. */
  readonly actionsAlwaysVisible: boolean;
  /** Id for the kebab so the open menu can reference it. */
  readonly kebabId: string;
  readonly menuOpen: boolean;
  readonly onOpenMenu: (queueId: string, anchor: HTMLButtonElement) => void;
  readonly onCloseMenu: () => void;
}

function openCountLabel(open: number | undefined, loading: boolean): string {
  if (open !== undefined) {
    return `${String(open)} open work item${open === 1 ? "" : "s"}`;
  }
  return loading ? "Open work items loading" : "Open work items unavailable";
}

export function QueueListRow({
  queue,
  index,
  selected,
  tabbable,
  onSelect,
  onFocusRow,
  rowRef,
  openCount,
  countLoading,
  hasActions,
  actionsAlwaysVisible,
  kebabId,
  menuOpen,
  onOpenMenu,
  onCloseMenu,
}: QueueListRowProps): ReactElement {
  // Rows on screen when the list fills animate nothing (the list's
  // `AnimatePresence` has `initial={false}`); a row that joins later fades in.
  const arrival = useListItemMotion(index, false);
  const reduced = usePrefersReducedMotion();
  // Hover reveal is CSS (`:hover`, `:focus-within` in the module). React
  // tracks focus only to decide whether the kebab is in the Tab sequence.
  const [focusWithin, setFocusWithin] = useState(false);

  const interactive = onSelect !== undefined;
  const actionsForced = hasActions && (actionsAlwaysVisible || selected || menuOpen);
  const kebabTabbable = actionsForced || focusWithin;

  const handleFocus = (event: FocusEvent<HTMLDivElement>): void => {
    setFocusWithin(true);
    if (event.target === event.currentTarget) {
      onFocusRow(index);
    }
  };

  const handleBlur = (event: FocusEvent<HTMLDivElement>): void => {
    const next = event.relatedTarget;
    if (!(next instanceof Node) || !event.currentTarget.contains(next)) {
      setFocusWithin(false);
    }
  };

  const handleRowKeyDown = (event: KeyboardEvent<HTMLDivElement>): void => {
    if (onSelect === undefined) return;
    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      onSelect(queue);
    }
  };

  const handleKebabClick = (event: MouseEvent<HTMLButtonElement>): void => {
    event.stopPropagation();
    if (menuOpen) {
      onCloseMenu();
    } else {
      onOpenMenu(queue.id, event.currentTarget);
    }
  };

  const handleKebabKeyDown = (event: KeyboardEvent<HTMLButtonElement>): void => {
    // Enter and Space activate the kebab, not the row behind it.
    if (event.key === "Enter" || event.key === " ") {
      event.stopPropagation();
      return;
    }
    if (event.key === "ArrowDown") {
      event.preventDefault();
      event.stopPropagation();
      onOpenMenu(queue.id, event.currentTarget);
    }
  };

  const interactiveProps = interactive
    ? {
        role: "button" as const,
        "aria-pressed": selected,
        onClick: () => onSelect(queue),
        onKeyDown: handleRowKeyDown,
      }
    : { role: "listitem" as const };

  const pressMotion =
    interactive && !reduced
      ? {
          whileTap: {
            scale: motionTokens.press.scale,
            transition: { duration: motionTokens.press.duration },
          },
        }
      : {};

  return (
    <motion.div
      ref={rowRef}
      {...interactiveProps}
      tabIndex={tabbable ? 0 : -1}
      className={styles.row}
      data-selected={selected || undefined}
      data-actions-visible={actionsForced || undefined}
      style={motionStyle({
        ...listRow("transparent", { hovered: false, selected }),
        // The resting fill is the CSS default; leaving it out lets `:hover`
        // in the module paint the tint.
        ...(selected ? {} : { background: undefined }),
        cursor: interactive ? "pointer" : "default",
      })}
      onFocus={handleFocus}
      onBlur={handleBlur}
      initial={arrival.initial}
      animate={arrival.animate}
      transition={arrival.transition}
      {...pressMotion}
    >
      {selected && <span aria-hidden="true" style={selectionBar} />}
      {queue.isPersonal && (
        <span
          role="img"
          aria-label="Personal queue"
          title="Personal queue"
          style={{
            width: 6,
            height: 6,
            borderRadius: radius.pill,
            background: color.accent.primary,
            flexShrink: 0,
          }}
        />
      )}
      <Text role="bodySm" weight={selected ? "semibold" : "medium"} truncate style={{ flex: 1 }}>
        {queue.name}
      </Text>
      <span
        role="img"
        title={openCountLabel(openCount, countLoading)}
        aria-label={openCountLabel(openCount, countLoading)}
        data-count-state={
          openCount !== undefined ? "ready" : countLoading ? "pending" : "unavailable"
        }
        style={{
          flexShrink: 0,
          minWidth: "2ch",
          textAlign: "right",
          // The cell keeps its width while the tally is in flight and the
          // number fades in; the text node never swaps in view.
          opacity: openCount === undefined && countLoading ? 0 : 1,
          transition: `opacity ${motionTokens.control.crossfade} ${motionTokens.easings.standard}`,
        }}
      >
        <Text role="caption" tone={selected ? "default" : "subtle"} tabular>
          {openCount ?? (countLoading ? "0" : "\u2014")}
        </Text>
      </span>
      {hasActions && (
        <span className={styles.actions}>
          <IconButton
            id={kebabId}
            icon={MoreVertical}
            size="sm"
            variant="ghost"
            aria-label={`Actions for ${queue.name}`}
            tooltip="More actions"
            aria-haspopup="menu"
            aria-expanded={menuOpen}
            tabIndex={kebabTabbable ? 0 : -1}
            onClick={handleKebabClick}
            onKeyDown={handleKebabKeyDown}
          />
        </span>
      )}
    </motion.div>
  );
}
